/**
 * The community screens' decisions, with no React (`D263`): how the curated
 * roadmap is grouped, which release notes are new to this person, and how a
 * release's day is written. The content itself is `@repo/contract/community`.
 */
import type { AppLocale } from "@repo/client/format";
import type {
  ReleaseNote,
  RoadmapItem,
  RoadmapStatus,
} from "@repo/contract/community";

/** The order the sheet draws the statuses in: what is closest first. */
export const STATUS_ORDER: readonly RoadmapStatus[] = [
  "building",
  "planned",
  "considering",
  "shipped",
];

export interface RoadmapGroup {
  readonly items: readonly RoadmapItem[];
  readonly status: RoadmapStatus;
}

/**
 * The items by status in {@link STATUS_ORDER}, each group in the curated
 * order — the owner's — except the shipped ones, newest release first. An
 * empty status draws no heading.
 */
export function roadmapGroups(
  items: readonly RoadmapItem[]
): readonly RoadmapGroup[] {
  return STATUS_ORDER.flatMap((status) => {
    const inStatus = items.filter((item) => item.status === status);
    if (inStatus.length === 0) {
      return [];
    }
    const ordered =
      status === "shipped"
        ? [...inStatus].sort((a, b) =>
            compareVersions(b.shippedIn ?? "0.0.0", a.shippedIn ?? "0.0.0")
          )
        : inStatus;
    return [{ items: ordered, status }];
  });
}

/** `major.minor.patch` compared numerically; a missing part is 0. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < 3; i += 1) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) {
      return diff;
    }
  }
  return 0;
}

/** How long a release stays news for somebody who has never opened the
 *  sheet on this device. */
export const NEWS_FRESH_DAYS = 30;
const DAY_MS = 86_400_000;

/**
 * The release notes this person has not seen.
 *
 * - **After the first open**, every release newer than the newest one the
 *   sheet showed them.
 * - **Before it** (`seen` is `null`: a new install, or the first bundle that
 *   has the sheet), only the releases of the last {@link NEWS_FRESH_DAYS}
 *   days. A person who installs months after a release is not told it is
 *   new; somebody updating into this feature is told what the update
 *   brought.
 */
export function unseenNotes(
  notes: readonly ReleaseNote[],
  seen: string | null,
  now: Date
): readonly ReleaseNote[] {
  if (seen === null) {
    return notes.filter(
      (note) =>
        now.getTime() - Date.parse(`${note.date}T00:00:00Z`) <=
        NEWS_FRESH_DAYS * DAY_MS
    );
  }
  return notes.filter((note) => compareVersions(note.version, seen) > 0);
}

/** "24 de out. de 2026" / "Oct 24, 2026" — the release's own calendar day,
 *  whatever the device's zone. */
export function releaseDay(date: string, locale: AppLocale): string {
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
    year: "numeric",
  }).format(new Date(`${date}T12:00:00Z`));
}
